import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import './reviews.css';
import { FaTelegramPlane } from 'react-icons/fa';
import { HiMiniArrowTurnLeftUp } from 'react-icons/hi2';
import { PiSmileySadLight, PiSmiley } from 'react-icons/pi';


const Stars = ({ count }) => {
  const totalStars = 5;
  return (
    <span className="stars">
      {'★'.repeat(count)}
      {'☆'.repeat(totalStars - count)}
    </span>
  );
};


const Smiley = ({ type }) => (
  <span className={`smiley ${type}`}>
    {type === 'good' ? <PiSmiley /> : <PiSmileySadLight />}
  </span>
);


const ContactIcon = ({ telegram }) => {
  if (!telegram) return null;
  return (
    <a
      href={`https://t.me/${telegram}`}
      target="_blank"
      rel="noopener noreferrer"
      className="contact-icon"
      title="Связаться в Telegram"
    >
      <FaTelegramPlane />
    </a>
  );
};


const Review = ({ review }) => (
  <div className="review-container">
    <div className="review">
      <div className="review-header">
        <div className="review-author">{review.name}</div>
        <Stars count={review.stars} />
      </div>
      <div className="review-text">{review.comment}</div>
      <div className="review-footer">
        <Smiley type={review.type} />
        <span className="review-date">
          {new Date(review.dateStr).toLocaleDateString('ru-RU')}
        </span>
        <ContactIcon telegram={review.telegram} />
      </div>
    </div>
    {review.answer && (
      <div className="admin-response-container">
        <div className="admin-response">
          <HiMiniArrowTurnLeftUp className="admin-arrow" />
          <div className="admin-text">
            <strong>Администрация:</strong>
            <p>{review.answer}</p>
          </div>
        </div>
      </div>
    )}
  </div>
);

const Reviews = () => {
  const [apiUrl, setApiUrl] = useState('');
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({ good: 0, bad: 0, total: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingApiUrl, setLoadingApiUrl] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const perPage = 20;

  
  useEffect(() => {
    const fetchApiUrl = async () => {
      try {
        const response = await axios.get('/templates/api.txt');
        setApiUrl(response.data.trim());
      } catch (err) {
        console.error('Ошибка при загрузке API URL:', err);
        setError('Не удалось загрузить конфигурацию API.');
      } finally {
        setLoadingApiUrl(false);
      }
    };
    fetchApiUrl();
  }, []);

  
  useEffect(() => {
    if (!apiUrl) return;
    const fetchReviews = async () => {
      setLoadingReviews(true);
      setError('');
      try {
        const response = await axios.get(`${apiUrl}api/reviews`, {
          params: {
            per_page: perPage,
            page: currentPage,
            filter: filter !== 'all' ? filter : undefined,
          },
        });
        
        const sortedReviews = [...response.data.reviews].sort(
          (a, b) => new Date(b.dateStr) - new Date(a.dateStr)
        );
        setReviews(sortedReviews);
        setCounts(response.data.counts);
        setTotalPages(response.data.total_pages);
      } catch (err) {
        console.error('Ошибка при загрузке отзывов:', err);
        setError('Не удалось загрузить отзывы.');
      } finally {
        setLoadingReviews(false);
      }
    };
    fetchReviews();
  }, [apiUrl, currentPage, filter]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(currentPage - Math.floor(maxPagesToShow / 2), 1);
    let endPage = startPage + maxPagesToShow - 1;
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(endPage - maxPagesToShow + 1, 1);
    }
    for (let page = startPage; page <= endPage; page++) {
      pages.push(
        <button
          key={page}
          className={`pagination-button ${page === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(page)}
          disabled={page === currentPage}
        >
          {page}
        </button>
      );
    }
    return (
      <div className="pagination">
        <button
          className="pagination-button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &laquo; Назад
        </button>
        {pages}
        <button
          className="pagination-button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Вперед &raquo;
        </button>
      </div>
    );
  };

  return (
    <div className="reviews-page">
      <Helmet>
        <title>Отзывы - Сигареты | магазин сигарет и табака</title>
        <meta
          name="description"
          content="Отзывы клиентов о магазине Сигареты. Узнайте, что говорят покупатели о качестве продукции и сервисе."
        />
        <meta
          name="keywords"
          content="отзывы, сигареты, магазин сигарет, табак, отзывы клиентов, магазин табака"
        />
      </Helmet>
      <h1 className="page-title">Отзывы</h1>
      {!loadingApiUrl && (
        <div className="reviews-counts">
          <button
            className={`count good ${filter === 'good' ? 'active-filter' : ''}`}
            onClick={() => handleFilterChange('good')}
          >
            Положительные: {counts.good}
          </button>
          <button
            className={`count bad ${filter === 'bad' ? 'active-filter' : ''}`}
            onClick={() => handleFilterChange('bad')}
          >
            Отрицательные: {counts.bad}
          </button>
          <button
            className={`count total ${filter === 'all' ? 'active-filter' : ''}`}
            onClick={() => handleFilterChange('all')}
          >
            Всего: {counts.total}
          </button>
        </div>
      )}
      {loadingApiUrl ? (
        <div className="loading">Загрузка конфигурации...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : loadingReviews ? (
        <div className="loading">Загрузка отзывов...</div>
      ) : reviews.length === 0 ? (
        <div className="no-reviews">Нет отзывов для отображения.</div>
      ) : (
        <>
          <div className="reviews-list">
            {reviews.map((review) => (
              <Review key={review.id} review={review} />
            ))}
          </div>
          {totalPages > 1 && renderPagination()}
        </>
      )}
    </div>
  );
};

export default Reviews;
